// @flow
import React from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import {
  Box,
  Divider,
  Stack,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import messages from '../messages';

type Props = {
  feeRows: Object,
};

const Currency = ({ amount }: { amount: number }) => (
  <FormattedNumber value={amount} style="currency" currency="USD" />
);

const FeeRow = ({
  label,
  description,
  amount,
}: {
  label: Object,
  description?: string,
  amount: number,
}) => (
  <Stack
    direction="row"
    justifyContent="space-between"
    alignItems="baseline"
    sx={{ mb: 1.5 }}
  >
    <Box>
      <Typography variant="bodyMedium">
        <FormattedMessage {...label} />
      </Typography>
      {description ? (
        <Typography variant="labelSmall" color="text.secondary">
          {description}
        </Typography>
      ) : null}
    </Box>
    <Typography variant="bodyMedium">
      <Currency amount={amount} />
    </Typography>
  </Stack>
);

// The two ledgers hold a different number of rows, so the totals are pushed to
// the foot of their column to keep them on one line with each other.
const COLUMN_SX = { flex: 1, display: 'flex', flexDirection: 'column' };

const LedgerTotal = ({ amount }: { amount: number }) => (
  <Box sx={{ mt: 'auto' }}>
    <Divider sx={{ mb: 1 }} />
    <Stack direction="row" justifyContent="space-between">
      <Typography variant="titleMedium">
        <FormattedMessage {...messages.totalToBeCharged} />
      </Typography>
      <Typography variant="titleMedium">
        <Currency amount={amount} />
      </Typography>
    </Stack>
  </Box>
);

const FeesAndDepositsList = ({ feeRows }: Props) => {
  const {
    applicationFee,
    applicationDescriptions,
    adminFee,
    adminDescriptions,
    depositAlternativeFee,
    securityDepositFee,
    residentLedgerTotal,
    securityLedgerTotal,
  } = feeRows;

  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={4}>
      <Box sx={COLUMN_SX}>
        <FeeRow
          label={messages.applicationFees}
          description={applicationDescriptions}
          amount={applicationFee}
        />
        <FeeRow
          label={messages.adminFees}
          description={adminDescriptions}
          amount={adminFee}
        />
        <FeeRow
          label={messages.depositAlternative}
          amount={depositAlternativeFee}
        />
        <LedgerTotal amount={residentLedgerTotal} />
      </Box>
      <Box sx={COLUMN_SX}>
        <FeeRow
          label={messages.securityLedgerDeposit}
          amount={securityDepositFee}
        />
        <LedgerTotal amount={securityLedgerTotal} />
      </Box>
    </Stack>
  );
};

export default FeesAndDepositsList;
